
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import PaperDraggable from "../../../../../../components/PaperDraggable"
import { useEffect, useMemo, useState } from "react";
import { IDataDetalharRecibo } from "../../../../../../types/api/financeiro/detalhar-recibos";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import axiosClient from "../../../../../../lib/axios";
import { useMutation, useQuery } from "react-query";
import { enqueueSnackbar } from "notistack";
import LabelPrinter from "../../../../../../components/LabelPrinter";

export default function DialogCancelarRecibo({
    open,
    handleClose,
    recibo,
    atualizarListaRecibos,
    handleChangeProcesso,
}: DialogCancelarReciboProps) {

    const [selected, setSelected] = useState<readonly number[]>([])
    const [form, setForm] = useState<TFormState>({
        numRecibo: 0,
        titulosRelacionados: []
    })
    const [processoEncontrado, setProcessoEncontrado] = useState<boolean | null>(null);
    const [codigoProcesso, setCodigoProcesso] = useState<string | null>(null)

    //querys e mutations
    const { mutate: SolicitarCancelamentoRecibo, isLoading: isMutating } = useMutation({
        mutationKey: 'financeiro/contas-receber/cancelar-recibo',
        mutationFn: (input: { numero_recibo: number, titulos: any[] }) => axiosClient.post(`financeiro/contas-receber/cancelar-recibo`, input),
        onError: (error) => {
            enqueueSnackbar(`Erro ao cancelar o recibo`, { variant: "error" });
        },
        onSuccess: (data) => {
            enqueueSnackbar(`Cancelamento concluido com sucesso!`, { variant: "success" });
            setOpenCancelRecibo(false);
            atualizarListaRecibos();
        }
    });

    const { isLoading, refetch } = useQuery({
        queryKey: ['financeiro/contas-receber/detalhar-recibo', recibo],
        queryFn: () => axiosClient.get("/financeiro/contas-receber/detalhar-recibo/" + recibo),
        enabled: false,
        cacheTime: 0,
        onSuccess: ({ data }) => {
            handleUpdateForm({
                numRecibo: recibo,
                titulosRelacionados: data.movimentacoes
            });
            setProcessoEncontrado(data.processo_cancelamento_em_execucao)
            if (data.processo_cancelamento_em_execucao && data.codigo_processo) {
                setCodigoProcesso(data.codigo_processo)
            }
        },
        onError: (error) => {
            console.error("Erro ao buscar detalhes do recibo:", error);
        }
    });

    const { total, qtdTitulos } = useMemo(() => {
        const titulosSelecionados = selected.map(index => form.titulosRelacionados[index]);

        const total = titulosSelecionados.reduce((total, item) => total + item.valor_movimento, 0)
        const qtdTitulos = String(titulosSelecionados.length)

        return { total, qtdTitulos }
    }, [selected, form.titulosRelacionados])

    const handleUpdateForm = (value: TFormState) => {
        setForm(value);
    }

    const setOpenCancelRecibo = (open: boolean) => {
        if (!open) {
            handleClose();
        }
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = form.titulosRelacionados.map((n, index) => index);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleOpenProcesso = () => {
        if (codigoProcesso) {
            handleChangeProcesso(codigoProcesso)
        }
    }

    const handleSubmit = () => {
        const titulosSelecionados = selected.map(index => form.titulosRelacionados[index]);

        if (titulosSelecionados.length === 0) {
            enqueueSnackbar("Nenhum título selecionado para cancelamento", { variant: "warning" });
            return;
        }

        SolicitarCancelamentoRecibo({
            numero_recibo: form.numRecibo,
            titulos: titulosSelecionados
        });
    };


    const handleClickRow = (_: React.MouseEvent<unknown>, id: number) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly number[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    useEffect(() => {
        refetch();
    }, []);

    const numSelected = selected.length
    const rowCount = form.titulosRelacionados.length
    const bloquearAvanco = selected.length === 0 || isLoading

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
            scroll="body"
            maxWidth={"md"}
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                Cancelar recibo
            </DialogTitle>

            <DialogContent>
                {
                    isLoading &&
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                }
                {
                    !isLoading && !processoEncontrado &&
                    <>
                        <Typography variant="subtitle1" color="textSecondary">
                            Selecionar títulos vinculados
                        </Typography>
                        <TableContainer sx={{ maxHeight: '400px' }}>
                            <Table aria-label="simple table" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                                checked={rowCount > 0 && numSelected === rowCount}
                                                color="primary"
                                                onChange={handleSelectAllClick}
                                                slotProps={{ input: { 'aria-label': 'selecionar títulos vinculados' } }}
                                            />
                                        </TableCell>

                                        <TableCell><b>Título</b></TableCell>

                                        <TableCell align="right"><b>Valor do movimento</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {form.titulosRelacionados.map((row, index) => {
                                        const isItemSelected = selected.includes(index);
                                        const labelId = `enhanced-table-checkbox-${row.titulo}`;

                                        return (
                                            <TableRow
                                                key={row.titulo}
                                                onClick={(event) => handleClickRow(event, index)}
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        color="primary"
                                                        checked={isItemSelected}
                                                        slotProps={{ input: { 'aria-labelledby': labelId } }}
                                                    />
                                                </TableCell>

                                                <TableCell component="th" scope="row">
                                                    {row.titulo}
                                                </TableCell>

                                                <TableCell align="right">{formatarValorMonetario(row.valor_movimento)}</TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                }

                {
                    !isLoading && processoEncontrado &&
                    <Box my={2}>
                        <Alert severity="warning" variant="outlined">
                            O recibo selecionado possui um processo de cancelamento em execução. <Link onClick={() => handleOpenProcesso()} sx={{ cursor: 'pointer' }}>Clique aqui</Link> para abrir o processo.
                        </Alert>
                    </Box>
                }

            </DialogContent>

            <DialogActions>
                <Box display="flex" justifyContent="space-between" width="100%" mt={2} px={2} pb={1}>
                    <Box display="flex" gap={4} justifyContent="flex-start">
                        {selected.length > 0 && (
                            <>
                                <LabelPrinter size='small' titulo='Total' valor={formatarValorMonetario(total)} />
                                <LabelPrinter size='small' titulo='Qtd. titulos' valor={qtdTitulos} />
                            </>
                        )}
                    </Box>

                    <Box display="flex" gap={2}>
                        <Button onClick={handleClose}>Fechar</Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={bloquearAvanco}
                            color="error"
                        >
                            {isMutating ? (
                                <>
                                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                                    Cancelando...
                                </>
                            ) : (
                                'Cancelar'
                            )}
                        </Button>
                    </Box>
                </Box>
            </DialogActions>

        </Dialog>
    )
}

interface DialogCancelarReciboProps {
    open: boolean,
    handleClose: () => void,
    recibo: number;
    handleChangeProcesso: (item: string) => void;
    atualizarListaRecibos: () => void;
}

export type TFormState = {
    numRecibo: number,
    titulosRelacionados: IDataDetalharRecibo[]
}
